import type { EndgameLesson } from "./endgames-extra";

// Rook, minor-piece and queen endings. Merged by ./endgames-extra.ts.
// Every starting result and every "only move" claim below was checked against the
// Lichess tablebase; every move and position replays in chess.js.
export const PIECE_ENDGAMES: EndgameLesson[] = [
  {
    endgame: {
      slug: "vancura-position",
      category: "Rook endings",
      level: "Intermediate",
      title: "The Vancura position: rook and rook's pawn against rook",
      summary: "Rook and a-pawn against rook is often a draw. Attack the pawn from the side, keep your king on g7 or h7, and check the white king away whenever it comes to protect the pawn.",
      fen: "R7/6k1/P4r2/1K6/8/8/8/8 b - - 0 1",
      side: "black",
      goal: "draw",
      sections: [
        {
          heading: "The set-up",
          body: [
            "White's rook stands in front of its pawn on a8, which is the worst square for it. Black's rook on f6 attacks the pawn from the side, so the white rook cannot leave the a-file without losing the pawn.",
            "Black's king belongs on g7 or h7. From there it covers g8 and h8, the squares the white rook would need for a skewer along the seventh rank once the pawn reaches a7. The position is named after Josef Vancura, who worked out this defence in the 1920s.",
          ],
        },
        {
          heading: "When the white king comes",
          body: [
            "White's only winning try is to bring the king to the pawn. Once the king protects the pawn, the rook on a8 is free to move. So the defender checks the king from the side the moment it arrives.",
            "In the starting position the king on b5 already guards a6, and Rf5+ is the only move that draws. If the king walks up to b6 and b7, keep checking along the ranks. If it hides on b8, it blocks its own rook, and your rook returns to f6 to attack the pawn again.",
          ],
        },
        {
          heading: "Typical mistakes",
          body: [
            "Going behind the pawn while it is still on a6 is a common error. With the white king as close as e3, for example, it loses: the king walks over to the pawn and frees its rook. The side attack is what makes the Vancura work.",
            "Letting your king drift away is the other classic error. With the pawn on a7, the black rook behind it and the black king on f7, White wins with Rh8. If Black takes the pawn, Rh7+ skewers the king and the rook.",
          ],
        },
      ],
      keyIdeas: ["Attack the pawn from the side", "King on g7 or h7", "Check the king when it guards the pawn", "Do not go behind a pawn on a6"],
    },
    beats: [
      { caption: "You defend as Black. The white rook is stuck in front of its pawn. Your rook on f6 attacks the pawn from the side, and your king on g7 guards g8 and h8.", camera: "black", spotlight: ["a8", "a6", "f6", "g7"], arrows: [{ from: "f6", to: "a6", tone: "red" }] },
      { caption: "The white king on b5 protects the pawn, which would free the rook. Rf5+ is the only drawing move: drive the king away at once.", move: "f6f5", camera: "black", after: { arrows: [{ from: "f5", to: "b5", tone: "red" }] } },
      { caption: "In this illustrative line the king steps up to b6, out of the check.", move: "b5b6", camera: "black" },
      { caption: "Rf6+ checks again along the sixth rank.", move: "f5f6", camera: "black", after: { arrows: [{ from: "f6", to: "b6", tone: "red" }] } },
      { caption: "The king tries b7.", move: "b6b7", camera: "black" },
      { caption: "Rf7+ keeps checking from the side.", move: "f6f7", camera: "black", after: { arrows: [{ from: "f7", to: "b7", tone: "red" }] } },
      { caption: "The king hides on b8. There it escapes the checks, but it also blocks its own rook on a8.", move: "b7b8", camera: "black", after: { spotlight: ["a8", "b8"] } },
      { caption: "Rf6 attacks the pawn again. White has made no progress, and the defence can start over.", move: "f7f6", camera: "black", after: { arrows: [{ from: "f6", to: "a6", tone: "red" }], labels: [{ sq: "f6", text: "Back to the side" }] } },
      { caption: "The mistake to avoid, in a separate example. The pawn is on a7 and the black rook is behind it, but the black king has wandered to f7.", fen: "R7/P4k2/8/8/8/8/r7/6K1 w - - 0 1", camera: "overview", spotlight: ["f7", "a2", "a8"] },
      { caption: "Rh8 is the only winning move. If Black takes the pawn, Rh7+ skewers the king and the rook on a7. With the king on g7, Rh8 would not be possible.", move: "a8h8", camera: "overview", after: { arrows: [{ from: "h8", to: "h7", tone: "gold" }, { from: "h7", to: "a7", tone: "red" }] } },
    ],
    guide: {
      goal: "Hold rook and rook's pawn against rook with a side attack on the pawn.",
      ideas: ["Attack the pawn from the side, not from behind.", "Keep your king on g7 or h7 to stop the skewer.", "Check the white king as soon as it protects the pawn."],
      pitfall: "A waiting move when the white king already guards the pawn lets the white rook leave a8 and the pawn advance.",
      question: "The white king steps next to its pawn. What should the defending rook do?",
      answers: ["Check the king from the side", "Move behind the pawn", "Wait with a king move"],
      correct: 0,
      explanation: "Once the king protects the pawn, the white rook is free. Checks from the side drive the king away and keep the rook tied to the pawn.",
    },
  },
  {
    endgame: {
      slug: "rook-vs-pawn",
      category: "Rook endings",
      level: "Intermediate",
      title: "Rook against pawn: cutting off the king",
      summary: "A rook usually beats a lone pawn, but not always. When your own king is far away, cut the defending king off from its pawn, and the rook wins the pawn on its own.",
      fen: "7K/8/8/8/1p2k3/8/8/7R w - - 0 1",
      side: "white",
      goal: "win",
      sections: [
        {
          heading: "When the rook wins",
          body: [
            "The rook wins easily if its king can get in front of the pawn, or if the rook can win the pawn before the defending king arrives to support it. A pawn that is far advanced and escorted by its king, against a rook whose king is far away, is often a draw.",
            "So count before you move. Here the white king on h8 is much too far away to help, and the black king on e4 is close enough to escort the b-pawn if it is allowed to.",
          ],
        },
        {
          heading: "Cutting the king off",
          body: [
            "Rd1 cuts the black king off along the d-file. The king cannot cross it to reach its pawn, and the pawn alone cannot outrun the rook: after Ke3, Rb1 stops it, and Rxb3+ wins it when it advances.",
            "Rd1 is not the only win. The check Rh4+ and the blockade Rb1 also win, but the cut is the idea to remember.",
          ],
        },
        {
          heading: "The wrong cut",
          body: [
            "Rc1 and Re1+ look similar but only draw. After either move the black king steps straight onto the d-file and joins its pawn.",
            "Cut the king off on the file right next to it. A cut one file further away leaves the king a file to walk onto, and a check that drives it towards the pawn helps the defender.",
          ],
        },
      ],
      keyIdeas: ["Count the kings' distances first", "Cut the king off from its pawn", "Use the file next to the king", "A lone pawn falls to the rook"],
    },
    beats: [
      { caption: "The black pawn on b4 has three steps to go, and the white king on h8 is far away. The rook has to stop the pawn on its own.", camera: "overview", spotlight: ["b4", "e4", "h1", "h8"] },
      { caption: "Rd1 cuts the king off along the d-file. The blue line is a wall the black king cannot cross.", move: "h1d1", camera: "top", after: { arrows: [{ from: "d1", to: "d8", tone: "blue" }] } },
      { caption: "In this illustrative line Black's king stays on the e-file. It cannot step onto the d-file to escort its pawn.", move: "e4e3", camera: "top" },
      { caption: "Rb1 stops the pawn from in front. The black king is still on the wrong side of the d-file.", move: "d1b1", camera: "top", after: { arrows: [{ from: "b1", to: "b4", tone: "red" }] } },
      { caption: "The pawn advances to b3.", move: "b4b3", camera: "top" },
      { caption: "Rxb3+ wins the pawn with check. Rook against a lone king is a simple win.", move: "b1b3", camera: "top", after: { labels: [{ sq: "b3", text: "Pawn won" }] } },
      { caption: "Compare Rc1, in a separate line. Kd3 steps onto the d-file at once, and the position is only a draw.", fen: "7K/8/8/8/1p2k3/8/8/2R5 b - - 0 1", move: "e4d3", camera: "top", after: { squares: [{ sq: "d3", tone: "red" }] } },
    ],
    guide: {
      goal: "Win rook against pawn by keeping the defending king away from its pawn.",
      ideas: ["Count how far both kings are from the pawn.", "Cut the defending king off on the file next to it.", "Without its king, the pawn falls to the rook."],
      pitfall: "A cut one file too far away, or a check that drives the king towards the pawn, lets the king join its pawn and draw.",
      question: "Your king is far away. What is the rook's first job?",
      answers: ["Chase the pawn from behind", "Give as many checks as possible", "Cut the defending king off from the pawn"],
      correct: 2,
      explanation: "If the king cannot reach its pawn, the pawn on its own is no match for the rook.",
    },
  },
  {
    endgame: {
      slug: "rook-behind-passed-pawn",
      category: "Rook endings",
      level: "Intermediate",
      title: "Rook behind the passed pawn",
      summary: "Rooks belong behind passed pawns. With White's rook behind the a-pawn and Black's rook stuck in front of it, the white king is free to walk over and decide the game. Swap the rooks and the same pawns only draw.",
      fen: "8/r5k1/P7/7p/7P/6K1/8/R7 w - - 0 1",
      side: "white",
      goal: "win",
      sections: [
        {
          heading: "Active and passive rooks",
          body: [
            "From a1 the white rook guards the a-pawn and supports its advance, and every step the pawn takes gives the rook more room. Black's rook on a7 has the opposite job: it has to block the pawn, and the further the pawn goes, the less it can do.",
            "With the rooks the other way round, the white rook in front of the pawn on a8 and the black rook behind it on a2, the same pawns only draw. Then it is White's rook that is tied down.",
          ],
        },
        {
          heading: "The plan",
          body: [
            "1.Ra4 keeps the rook behind the pawn and also guards the h4 pawn along the fourth rank. That frees the white king, which walks across to the a-pawn: Kf4, Ke4, Kd5, Kc5 and Kb6. Black's checks only slow it down.",
            "If the black king sets off after the a-pawn instead, it leaves its own h-pawn behind. Black cannot cover both sides at once.",
          ],
        },
        {
          heading: "Typical mistakes",
          body: [
            "In the starting position every rook move that leaves the a-file throws the win away. The rook's place is behind the pawn.",
            "The pawn does not need to hurry. In the line shown, a7 comes only once the king stands on b6 to support it.",
          ],
        },
      ],
      keyIdeas: ["Put your rook behind the passed pawn", "Keep the enemy rook passive in front", "Free the king and march it over", "Push when the king supports the pawn"],
    },
    beats: [
      { caption: "White's rook stands behind the a-pawn. Black's rook has to block it from a7, a passive post.", camera: "overview", spotlight: ["a1", "a6", "a7"], arrows: [{ from: "a1", to: "a6", tone: "green" }] },
      { caption: "Ra4 keeps the rook behind the pawn and also guards h4 along the fourth rank. The white king is now free.", move: "a1a4", camera: "overview", after: { arrows: [{ from: "a4", to: "h4", tone: "blue" }] } },
      { caption: "In this illustrative line Black waits with the king.", move: "g7f6", camera: "overview" },
      { caption: "Kf4 starts the king's walk to the queenside.", move: "g3f4", camera: "overview", after: { arrows: [{ from: "f4", to: "b6", tone: "green" }] } },
      { caption: "After Kg6, Ke4 Re7+, Kd5 Ra7, Kc5 Rc7+ and Kb6, the king has reached the pawn. Black's rook has run out of useful checks and leaves the seventh rank.", fen: "8/2r5/PK4k1/7p/R6P/8/8/8 b - - 0 1", move: "c7c1", camera: "queenside" },
      { caption: "a7. The rook still guards the pawn from behind, and the king on b6 supports it too.", move: "a6a7", camera: "queenside", after: { squares: [{ sq: "a8", tone: "gold" }] } },
      { caption: "Compare the same pawns with the rooks swapped. The white rook in front of its pawn is now the passive one, and this position is only a draw.", fen: "R7/6k1/P7/7p/7P/6K1/r7/8 w - - 0 1", camera: "overview", spotlight: ["a8", "a2"] },
    ],
    guide: {
      goal: "Use a rook behind a passed pawn to tie down the defender.",
      ideas: ["Your rook behind the pawn gains room as the pawn advances.", "The defending rook in front of the pawn is passive.", "With the rook doing the guarding, your king is free to act."],
      pitfall: "Moving the rook off the pawn's file, or in front of the pawn, hands the active role to the defender's rook.",
      question: "Where does a rook usually belong in relation to a passed pawn?",
      answers: ["Behind it", "In front of it", "On the rank beside it"],
      correct: 0,
      explanation: "Behind the pawn the rook protects it and gains scope with every advance, while a rook in front of it only gets more cramped.",
    },
  },
  {
    endgame: {
      slug: "short-side-defence",
      category: "Rook endings",
      level: "Intermediate",
      title: "The short-side defence in rook endings",
      summary: "When your king is pushed off the pawn's file, send it to the short side and keep your rook on the long side, where it has room to check the enemy king from a distance.",
      fen: "5k2/R7/5K2/5P2/8/8/8/1r6 b - - 0 1",
      side: "black",
      goal: "draw",
      sections: [
        {
          heading: "Short side and long side",
          body: [
            "The f-pawn splits the board in two. Towards the h-file there are only two files, g and h: the short side. Towards the a-file there are five: the long side.",
            "The rule is to put the king on the short side and the rook on the long side. From there the rook can check the white king from the side, from far enough away that the king cannot chase it off without leaving its pawn.",
          ],
        },
        {
          heading: "In this position",
          body: [
            "Black's king on f8 has to choose. Kg8, to the short side, draws, and so does the immediate check Rb6+. Ke8, to the long side, loses.",
            "After Kg8 Ke6, Rb6+ checks from the long side. If the king steps to e7, the rook goes back to b1, ready to check again, and after f6 the check Re1+ holds.",
          ],
        },
        {
          heading: "Why the long side loses",
          body: [
            "After Ke8, Ra8+ drives the king to d7, and White answers Kf7. Now the black king stands on the seventh rank between its own rook and the white king, so the rook cannot check along that rank, and the white king shelters in front of its pawn.",
          ],
        },
      ],
      keyIdeas: ["King to the short side", "Rook to the long side", "Check from a distance", "Do not block your own rook"],
    },
    beats: [
      { caption: "You defend as Black. The white king on f6 has pushed your king off the pawn's file, and the white rook on a7 keeps it on the back rank. Which way should the king go?", camera: "black", spotlight: ["f8", "f6", "f5", "b1"] },
      { caption: "Kg8 goes to the short side, towards the h-file. That leaves the long side free for the rook.", move: "f8g8", camera: "black", after: { squares: [{ sq: "g8", tone: "green" }] } },
      { caption: "In this illustrative line White's king steps to e6, heading for e7.", move: "f6e6", camera: "black" },
      { caption: "Rb6+ checks from the long side, four files from the pawn.", move: "b1b6", camera: "black", after: { arrows: [{ from: "b6", to: "e6", tone: "red" }] } },
      { caption: "The king steps to e7.", move: "e6e7", camera: "black" },
      { caption: "Rb1 takes the rook back to a safe distance, ready to check again. After f6, Re1+ holds the draw.", move: "b6b1", camera: "black", after: { labels: [{ sq: "b1", text: "Long side" }] } },
      { caption: "Compare the wrong choice, in a separate line: the king went to e8, the long side. White to move.", fen: "4k3/R7/5K2/5P2/8/8/8/1r6 w - - 0 1", camera: "black", spotlight: ["e8", "b1"] },
      { caption: "Ra8+ drives the king further away.", move: "a7a8", camera: "black", after: { arrows: [{ from: "a8", to: "e8", tone: "red" }] } },
      { caption: "The king goes to d7.", move: "e8d7", camera: "black" },
      { caption: "Kf7. The black king now stands on the seventh rank between its rook and the white king, so there are no side checks along that rank. This position is lost.", move: "f6f7", camera: "black", after: { spotlight: ["d7", "f7"] } },
    ],
    guide: {
      goal: "Draw a rook ending by sending your king to the short side.",
      ideas: ["The pawn splits the board into a short side and a long side.", "Your king goes to the short side.", "Your rook checks from the long side, far from the pawn."],
      pitfall: "Putting your king on the long side blocks your own rook's side checks.",
      question: "Your king has been pushed off the pawn's file. Where should it go?",
      answers: ["To the long side", "To the short side", "In front of the enemy king"],
      correct: 1,
      explanation: "On the short side the king stays out of its rook's way, leaving the long side for checks from a distance.",
    },
  },
  {
    endgame: {
      slug: "queen-vs-pawn-seventh",
      category: "Queen endings",
      level: "Beginner",
      title: "Queen against pawn on the seventh: the winning method",
      summary: "A queen beats a centre or knight's pawn on the seventh rank even when its own king is far away. Check until the defending king blocks its pawn, then use the free move to bring your king closer.",
      fen: "1Q5K/8/8/8/8/8/4p3/3k4 w - - 0 1",
      side: "white",
      goal: "win",
      sections: [
        {
          heading: "The method",
          body: [
            "Black's pawn is one step from queening, and the white king on h8 is far away. The queen cannot stop the pawn by force at once, but it can gain time with checks and pins.",
            "Keep checking until the black king has to stand on e1, in front of its own pawn. Then the pawn cannot move, and White has one free move. Spend it on bringing the king one step closer, then start the checks again.",
            "Repeat the cycle until the white king is close enough to help win the pawn or mate.",
          ],
        },
        {
          heading: "Which pawns lose",
          body: [
            "The method works against a centre pawn (on the d- or e-file) and a knight's pawn (on the b- or g-file). The defender has no way out: the only way to keep the pawn is to block it with the king.",
            "Against a bishop's or rook's pawn the defender has a stalemate defence, and with the attacking king far away the result is a draw. That is the next lesson.",
          ],
        },
        {
          heading: "Typical mistakes",
          body: [
            "In the starting position only checks win: Qb1+, Qb3+, Qd6+ and Qd8+. Every quiet queen or king move gives up the win.",
            "Do not take the pawn while the king defends it. And when the king finally stands on e1, Qxe2+ throws the queen away: the free move belongs to your king.",
          ],
        },
      ],
      keyIdeas: ["Check or pin to gain time", "Force the king in front of the pawn", "Use the free move for your king", "Repeat until the king arrives"],
    },
    beats: [
      { caption: "The pawn on e2 is one step from queening, and the white king on h8 is far away. White starts with checks.", camera: "overview", spotlight: ["e2", "d1", "b8", "h8"] },
      { caption: "Qb3+ checks along the diagonal towards d1.", move: "b8b3", camera: "overview", after: { arrows: [{ from: "b3", to: "d1", tone: "red" }] } },
      { caption: "The king steps to d2 and still guards the pawn.", move: "d1d2", camera: "overview" },
      { caption: "Qb2+ checks along the second rank. Each check brings the queen closer to the pawn.", move: "b3b2", camera: "overview", after: { arrows: [{ from: "b2", to: "d2", tone: "red" }] } },
      { caption: "The king returns to d1.", move: "d2d1", camera: "overview" },
      { caption: "A few checks later, after Qd4+ Kc2, Qe3 Kd1 and Qd3+, the queen stands on d3. Ke1 is the only way to keep the pawn defended, and now the king blocks its own pawn.", fen: "7K/8/8/8/8/3Q4/4p3/3k4 b - - 0 1", move: "d1e1", camera: "overview", after: { squares: [{ sq: "e1", tone: "gold", label: "Blocked" }] } },
      { caption: "The pawn cannot move, so White has a free move. Kg7 brings the king one step closer. Then the checks begin again.", move: "h8g7", camera: "overview", after: { arrows: [{ from: "g7", to: "f6", tone: "green" }] } },
      { caption: "Repeat the cycle until the king arrives. In this separate finishing position the white king on f3 guards e2.", fen: "8/8/8/8/8/3Q1K2/4p3/4k3 w - - 0 1", camera: { focus: "e2", distance: 11 }, spotlight: ["d3", "f3", "e1"] },
      { caption: "Qxe2 is checkmate. The king on f3 protects the queen, and every escape square is covered.", move: "d3e2", camera: { focus: "e2", distance: 11 }, after: { labels: [{ sq: "e1", text: "Checkmate" }] } },
    ],
    guide: {
      goal: "Beat a pawn on the seventh by gaining time with checks.",
      ideas: ["Check or pin until the king blocks its own pawn.", "Use the free move to bring your king closer.", "Repeat until your king can help."],
      pitfall: "A quiet queen move lets the defender queen or escape the cycle. Keep checking until the king stands in front of the pawn.",
      question: "The defending king has just stepped in front of its pawn. What do you do with the free move?",
      answers: ["Take the pawn with the queen", "Bring your king one step closer", "Move the queen far away"],
      correct: 1,
      explanation: "The blocked pawn cannot move, so the tempo goes to your king. Each cycle brings it one step closer until it can help.",
    },
  },
  {
    endgame: {
      slug: "queen-vs-bishop-pawn",
      category: "Queen endings",
      level: "Beginner",
      title: "Queen against pawn on the seventh: the bishop's and rook's pawn draw",
      summary: "Against a bishop's or rook's pawn on the seventh, the defending king runs to the corner instead of blocking the pawn. Taking the pawn then gives stalemate, so the queen never gains the time it needs.",
      fen: "8/8/1Q6/4K3/8/8/1kp5/8 b - - 0 1",
      side: "black",
      goal: "draw",
      sections: [
        {
          heading: "The stalemate defence",
          body: [
            "Against a pawn on the seventh the queen wins by forcing the defending king in front of its pawn, then using the free move to bring its own king closer. Against a pawn on the c-file that plan fails.",
            "When the queen checks, the black king goes to a1 instead of c1. If the queen takes the pawn on c2 while the king stands on a1, Black has no legal move and is not in check: stalemate. So the queen never wins the free move it needs to bring its own king closer.",
          ],
        },
        {
          heading: "Choose the right square",
          body: [
            "In the starting position you are in check. Ka1, Ka2 and Ka3 all hold the draw. Kc1, the natural move to block the pawn, loses, and so does Kc3.",
            "The same choice comes back later. In the demonstration, after Qc3+ only Kb1 draws, and after Qb3+ only Ka1 draws. Each time, blocking the pawn on c1 would hand White the free move it needs.",
          ],
        },
        {
          heading: "The rook's pawn, and the exceptions",
          body: [
            "With a rook's pawn the defence is simpler: the king hides in the corner in front of its pawn, and any attempt to take away its last squares gives stalemate.",
            "Both draws rely on the attacking king being too far away to join in. When it is close, the queen often wins, so check the exact position before relying on the corner.",
          ],
        },
      ],
      keyIdeas: ["Run to the corner, not in front of the pawn", "Qxc2 would be stalemate", "Deny White the free move", "Works when the white king is far away"],
    },
    beats: [
      { caption: "You defend as Black, in check from the queen on b6. The white king on e5 is too far away to help in time, if you defend correctly.", camera: "black-queenside", spotlight: ["b2", "c2", "b6"], arrows: [{ from: "b6", to: "b2", tone: "red" }] },
      { caption: "Ka1 heads for the corner. Kc1, blocking the pawn, would lose.", move: "b2a1", camera: "black-queenside", after: { squares: [{ sq: "a1", tone: "green" }, { sq: "c1", tone: "red" }] } },
      { caption: "Qa5+ checks along the a-file.", move: "b6a5", camera: "black-queenside", after: { arrows: [{ from: "a5", to: "a1", tone: "red" }] } },
      { caption: "Kb1 steps out of check. The pawn still threatens to queen.", move: "a1b1", camera: "black-queenside" },
      { caption: "A few checks later, after Qb4+ Ka1 and Qc3+, Kb1 is the only drawing move. Ka2 would lose.", fen: "8/8/8/4K3/8/2Q5/2p5/k7 b - - 0 1", move: "a1b1", camera: "black-queenside" },
      { caption: "Qb3+ checks again.", move: "c3b3", camera: "black-queenside", after: { arrows: [{ from: "b3", to: "b1", tone: "red" }] } },
      { caption: "Ka1 is the only drawing move. Kc1 would block the pawn and give White a free move for the king.", move: "b1a1", camera: "black-queenside", after: { squares: [{ sq: "a1", tone: "green" }] } },
      { caption: "Qxc2 wins the pawn, but Black has no legal move and is not in check. It is stalemate, and the game is drawn.", move: "b3c2", camera: "black-queenside", after: { labels: [{ sq: "a1", text: "Stalemate" }] } },
    ],
    guide: {
      goal: "Draw against the queen with the stalemate defence.",
      ideas: ["Run to the corner instead of blocking the pawn.", "Taking the pawn then leaves you with no legal move.", "Without free moves, the far-away king never arrives."],
      pitfall: "Blocking the pawn with your king, as on c1, is the natural move and it loses: it gives the queen the free move it is looking for.",
      question: "Why can the queen not take the c2 pawn when the black king is on a1?",
      answers: ["The pawn is protected by the king", "It would be stalemate", "The queen would be pinned"],
      correct: 1,
      explanation: "With the queen on c2, the king on a1 has no legal move and is not in check, so the game ends in stalemate.",
    },
  },
  {
    endgame: {
      slug: "queen-vs-rook",
      category: "Queen endings",
      level: "Intermediate",
      title: "Queen against rook: the Philidor position",
      summary: "The queen beats a lone rook, but the defender keeps the rook beside its king. In the Philidor position, lose a tempo with the queen and the rook has to leave its king and be won.",
      fen: "1k6/1r6/2K5/Q7/8/8/8/8 w - - 0 1",
      side: "white",
      goal: "win",
      sections: [
        {
          heading: "The Philidor position",
          body: [
            "The rook on b7 is guarded by its king and blocks the queen's checks. If it were Black to move, every move would lose: the rook would have to leave its king, and the queen would then win it with forking checks or mate. A position where the side to move must make a damaging move is called zugzwang.",
            "The position carries the name of Philidor, who analysed it in the eighteenth century.",
          ],
        },
        {
          heading: "Losing a tempo",
          body: [
            "With White to move, the queen makes a triangle to pass the move to Black: Qe5+ Ka8, Qa1+ Kb8, Qa5. The pieces stand where they began, but now Black is to move.",
            "The rook's toughest try is to run far away, as with Rb1. Then a series of checks wins it: Qd8+ Ka7, Qd4+ Ka8, Qh8+ Ka7, and Qh7+ attacks the king and the rook on b1 at once.",
          ],
        },
        {
          heading: "Typical mistakes",
          body: [
            "Natural moves can throw the win away. From the start, Qb5, Qc5 and Qb6 only draw, and Qc3 walks into Rc7+, which skewers the king on c6 and the queen behind it.",
            "Keep your king close to the defending king, and before every move look for rook checks that skewer, and for rook sacrifices that leave the defending king stalemated.",
          ],
        },
      ],
      keyIdeas: ["Recognise the zugzwang", "Triangulate with the queen to lose a tempo", "Win the rook with forks and skewers", "Watch for stalemate tricks"],
    },
    beats: [
      { caption: "The black rook on b7 shelters beside its king. White's king on c6 and queen on a5 surround it. It is White to move, and that is the difficulty.", camera: "queenside", spotlight: ["b7", "b8", "c6", "a5"] },
      { caption: "Qe5+ checks along the diagonal towards b8.", move: "a5e5", camera: "queenside", after: { arrows: [{ from: "e5", to: "b8", tone: "red" }] } },
      { caption: "The king steps into the corner.", move: "b8a8", camera: "queenside" },
      { caption: "Qa1+ checks along the a-file.", move: "e5a1", camera: "queenside", after: { arrows: [{ from: "a1", to: "a8", tone: "red" }] } },
      { caption: "Kb8 returns. Blocking with Ra7 would allow mate at once.", move: "a8b8", camera: "queenside" },
      { caption: "Qa5 completes the triangle. The position is the same as at the start, but now Black must move: zugzwang.", move: "a1a5", camera: "queenside", after: { arrows: [{ from: "a5", to: "e5", tone: "blue" }, { from: "e5", to: "a1", tone: "blue" }, { from: "a1", to: "a5", tone: "blue" }], labels: [{ sq: "b7", text: "Zugzwang" }] } },
      { caption: "The rook has to leave its king. Running far away with Rb1 is the toughest defence.", move: "b7b1", camera: "overview" },
      { caption: "After Qd8+ Ka7, Qd4+ Ka8 and Qh8+ Ka7, the queen has reached h8. Qh7+ attacks the king and the rook on b1 together.", fen: "7Q/k7/2K5/8/8/8/8/1r6 w - - 0 1", move: "h8h7", camera: "overview", after: { arrows: [{ from: "h7", to: "a7", tone: "red" }, { from: "h7", to: "b1", tone: "red" }] } },
      { caption: "The king must step out of check.", move: "a7b8", camera: "overview" },
      { caption: "Qxb1+ wins the rook. King and queen against king is a simple mate.", move: "h7b1", camera: "overview", after: { labels: [{ sq: "b1", text: "Rook won" }] } },
    ],
    guide: {
      goal: "Win the rook from the Philidor position by passing the move.",
      ideas: ["With Black to move, every rook move loses.", "A queen triangle hands the move to Black.", "Once the rook leaves its king, forking checks win it."],
      pitfall: "Checks that do not lead anywhere, or a queen on the same line as your king, give the rook a chance to skewer or force stalemate.",
      question: "Why does White make a triangle with the queen from the starting position?",
      answers: ["To win the rook immediately", "To give Black the move in the same position", "To bring the king to the corner"],
      correct: 1,
      explanation: "The position is zugzwang for the side to move. The triangle returns the queen to a5 with Black to move, so the rook must leave its king.",
    },
  },
  {
    endgame: {
      slug: "opposite-coloured-bishops",
      category: "Minor pieces",
      level: "Intermediate",
      title: "Opposite-coloured bishops: drawing two pawns down",
      summary: "With bishops on opposite colours, even two extra pawns are often not enough to win. Blockade the pawns on squares of your bishop's colour, where the enemy bishop can never reach you.",
      fen: "8/6b1/3k4/3P4/4PK2/8/8/5B2 b - - 0 1",
      side: "black",
      goal: "draw",
      sections: [
        {
          heading: "Why the bishops never meet",
          body: [
            "White's bishop moves on light squares and Black's on dark squares. They can never attack each other, and neither can contest the squares the other controls. That makes blockades very hard to break.",
          ],
        },
        {
          heading: "The fortress",
          body: [
            "Black's king stands on d6, in front of the d-pawn and on a dark square, so the white bishop can never check it or drive it away. The e-pawn needs to reach e5, another dark square, which the king and the bishop on the long diagonal both control.",
            "If White pushes e5, Black simply takes the pawn. Otherwise Black's bishop shuffles along the long diagonal, and White has no way to make progress.",
          ],
        },
        {
          heading: "Typical mistakes",
          body: [
            "The king must hold its post. In the starting position Kc7, Kd7 and Ke7 all lose: once the king leaves d6, the d-pawn can move on.",
            "Most bishop waiting moves are fine, since the king on d6 also covers e5. Just keep the bishop out of reach of the white king: after Kf5, for example, Bf6 loses the bishop to Kxf6.",
            "Opposite-coloured bishops do not draw every time. Pawns far apart, which the king and bishop cannot cover together, can still win.",
          ],
        },
      ],
      keyIdeas: ["Bishops on opposite colours never meet", "Blockade on your bishop's colour", "Keep the king on d6", "Let the bishop wait on the long diagonal"],
    },
    beats: [
      { caption: "You defend as Black, two pawns down. Your king on d6 blocks the d-pawn, and your bishop covers e5, the square the e-pawn needs.", camera: "black", spotlight: ["d6", "g7", "d5", "e4"], squares: [{ sq: "d6", tone: "green" }, { sq: "e5", tone: "blue" }] },
      { caption: "Bc3 waits on the long diagonal, still watching e5.", move: "g7c3", camera: "black", after: { arrows: [{ from: "c3", to: "e5", tone: "blue" }] } },
      { caption: "In this illustrative line White's king tries to find a way in.", move: "f4f5", camera: "black" },
      { caption: "Bb2 waits again, still covering e5.", move: "c3b2", camera: "black", after: { arrows: [{ from: "b2", to: "e5", tone: "blue" }] } },
      { caption: "e5+ tries to force a way through.", move: "e4e5", camera: "black" },
      { caption: "Bxe5. The pawn is gone, and the blockade stands. The game is still a draw.", move: "b2e5", camera: "black", after: { labels: [{ sq: "e5", text: "Blockade holds" }] } },
      { caption: "The mistake to avoid, back in the starting position: Kd7 leaves the blockade square, and the position is lost.", fen: "8/6b1/3k4/3P4/4PK2/8/8/5B2 b - - 0 1", move: "d6d7", camera: "black", after: { arrows: [{ from: "d5", to: "d6", tone: "red" }] } },
    ],
    guide: {
      goal: "Hold a draw two pawns down with a blockade on your bishop's colour.",
      ideas: ["Opposite-coloured bishops can never attack each other.", "Blockade on squares of your bishop's colour.", "Keep the king in place and let the bishop wait."],
      pitfall: "Moving the king off its blockade square, even for one move, lets the pawns advance.",
      question: "On which squares should the defender build the blockade?",
      answers: ["Squares of the attacker's bishop colour", "Squares of the defender's bishop colour", "Any squares on the edge of the board"],
      correct: 1,
      explanation: "The attacker's bishop can never attack those squares, so a king or bishop standing guard there cannot be driven away.",
    },
  },
  {
    endgame: {
      slug: "saavedra-position",
      category: "Rook endings",
      level: "Intermediate",
      title: "The Saavedra position: promoting to a rook",
      summary: "The most famous endgame study. The pawn runs, the black rook chases the king with checks, and the winning move is a promotion to a rook, because a new queen would allow stalemate.",
      fen: "8/8/1KP5/3r4/8/8/8/k7 w - - 0 1",
      side: "white",
      goal: "win",
      sections: [
        {
          heading: "Escaping the checks",
          body: [
            "1.c7 is the only winning move; every king move only draws. Black's rook cannot stop the pawn directly, so it checks: 1...Rd6+ 2.Kb5 Rd5+ 3.Kb4 Rd4+ 4.Kb3 Rd3+ 5.Kc2. The king walks down the board towards the rook until the checks run out.",
            "On the second move only 2.Kb5 wins. 2.Kc5 lets the rook drop to the first rank and check from behind on the c-file, and the game is drawn. The position is named after Fernando Saavedra, who found the winning idea in 1895.",
          ],
        },
        {
          heading: "The stalemate trap",
          body: [
            "With no checks left, Black plays 5...Rd4. If 6.c8=Q, then 6...Rc4+ 7.Qxc4 is stalemate: the black king on a1 has no legal move. Any king move instead lets the rook take the queen on c8.",
          ],
        },
        {
          heading: "The rook promotion",
          body: [
            "6.c8=R avoids the stalemate and threatens Ra8+, which leads to mate on the a-file. Black stops that with 6...Ra4, and then 7.Kb3 attacks the rook and threatens Rc1 mate at the same time. Black cannot meet both threats.",
            "Promoting to a queen draws, and a bishop or a knight even loses. Before promoting, always ask what the opponent can do against each choice.",
          ],
        },
      ],
      keyIdeas: ["Push the pawn at once", "Walk the king towards the rook", "Spot the stalemate trap", "Promote to a rook"],
    },
    beats: [
      { caption: "White's pawn is two steps from queening, and Black's rook waits on the d-file. 1.c7 is the only winning move.", move: "c6c7", camera: "overview", spotlight: ["c6", "d5", "a1"], after: { arrows: [{ from: "c7", to: "c8", tone: "gold" }] } },
      { caption: "The rook cannot stop the pawn directly, so the checks begin.", move: "d5d6", camera: "overview", after: { arrows: [{ from: "d6", to: "b6", tone: "red" }] } },
      { caption: "Kb5 is the only winning reply. The king heads down the board towards the rook.", move: "b6b5", camera: "overview" },
      { caption: "After Rd5+ Kb4, Rd4+ Kb3 and Rd3+ Kc2, the checks have run out. Black now plays Rd4 and sets a trap.", fen: "8/2P5/8/8/8/3r4/2K5/k7 b - - 0 1", move: "d3d4", camera: "queenside", after: { arrows: [{ from: "d4", to: "c4", tone: "violet" }] } },
      { caption: "The natural move, shown here as the mistake: c8=Q.", move: "c7c8q", camera: "queenside" },
      { caption: "Rc4+ offers the rook.", move: "d4c4", camera: "queenside", after: { arrows: [{ from: "c4", to: "c2", tone: "red" }] } },
      { caption: "Qxc4 is forced to keep the queen, and it is stalemate. The black king on a1 has no legal move.", move: "c8c4", camera: "queenside", after: { labels: [{ sq: "a1", text: "Stalemate" }] } },
      { caption: "Back to the critical moment. c8=R avoids the stalemate and threatens mate on the a-file.", fen: "8/2P5/8/8/3r4/8/2K5/k7 w - - 0 1", move: "c7c8r", camera: "queenside", after: { arrows: [{ from: "c8", to: "a8", tone: "gold" }], labels: [{ sq: "c8", text: "Rook" }] } },
      { caption: "Ra4 stops the mate on the a-file.", move: "d4a4", camera: "queenside" },
      { caption: "Kb3 attacks the rook and threatens Rc1 mate. Black cannot meet both threats.", move: "c2b3", camera: "queenside", after: { arrows: [{ from: "b3", to: "a4", tone: "red" }, { from: "c8", to: "c1", tone: "gold" }] } },
    ],
    guide: {
      goal: "See why the right promotion piece is a rook.",
      ideas: ["Push the pawn and let the king walk towards the checking rook.", "A new queen would allow a stalemate sacrifice.", "A rook promotion keeps the mating threats without stalemate."],
      pitfall: "Promoting to a queen by habit. Here Rc4+ forces Qxc4, and the black king on a1 is stalemated.",
      question: "Why does White promote to a rook instead of a queen?",
      answers: ["A queen would be captured for free", "A queen allows a stalemate defence", "A rook can move like a knight"],
      correct: 1,
      explanation: "After c8=Q, Rc4+ forces Qxc4 and stalemate. The rook does the job without taking away the king's last squares.",
    },
  },
];
